import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

const PAGE_W = 595.28;
const PAGE_H = 841.89;
const MARGIN = 48;

export const PDF_COLORS = {
  navy: rgb(2 / 255, 12 / 255, 105 / 255),
  dark: rgb(0.13, 0.13, 0.13),
  muted: rgb(0.4, 0.4, 0.4),
  line: rgb(0.8, 0.8, 0.8),
  green: rgb(0, 0.5, 0),
  red: rgb(0.85, 0, 0),
  orange: rgb(0.85, 0.45, 0),
  amber: rgb(0.7, 0.33, 0.04),
  brandGreen: rgb(0.36, 0.77, 0.16),
  panel: rgb(0.95, 0.96, 0.99),
  white: rgb(1, 1, 1),
};

const REPLACEMENTS = {
  '\u2013': '-',
  '\u2014': '-',
  '\u2018': "'",
  '\u2019': "'",
  '\u201C': '"',
  '\u201D': '"',
  '\u2022': '-',
  '\u00B7': '-',
  '\u2713': '',
  '\u2714': '',
  '\u2717': '',
  '\u2718': '',
  '\u00B5': 'u',
};

export function sanitizePdfText(value) {
  let s = String(value ?? '');
  s = s.replace(/⚠️/g, 'WARNING').replace(/🚨/g, 'ALERT').replace(/📷/g, '');
  s = s.replace(/[^\x00-\xff]/g, (ch) => REPLACEMENTS[ch] || '');
  return s;
}

export function formatYesNo(value) {
  if (value === 'yes' || value === true) return 'YES';
  if (value === 'no' || value === false) return 'NO';
  return 'N/A';
}

export function yesNoColor(value) {
  if (value === 'yes' || value === true || value === 'PASS' || value === 'Passed') return PDF_COLORS.green;
  if (value === 'no' || value === false || value === 'FAIL' || value === 'Rejected') return PDF_COLORS.red;
  if (value === 'Passed with touch-ups') return PDF_COLORS.orange;
  if (value === 'Hold for rework') return PDF_COLORS.orange;
  if (value === 'High') return PDF_COLORS.red;
  if (value === 'Medium') return PDF_COLORS.orange;
  if (value === 'Low') return PDF_COLORS.green;
  return PDF_COLORS.muted;
}

export function passFailColor(pass) {
  if (pass === true || pass === 'PASS') return PDF_COLORS.green;
  if (pass === false || pass === 'FAIL') return PDF_COLORS.red;
  return PDF_COLORS.muted;
}

function wrapText(text, font, size, maxWidth) {
  const raw = sanitizePdfText(text);
  if (!raw) return [''];
  const words = raw.split(/\s+/);
  const lines = [];
  let current = '';

  const pushChunked = (word) => {
    let chunk = '';
    for (const ch of word) {
      const test = chunk + ch;
      if (font.widthOfTextAtSize(test, size) <= maxWidth) {
        chunk = test;
      } else {
        if (chunk) lines.push(chunk);
        chunk = ch;
      }
    }
    return chunk;
  };

  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (font.widthOfTextAtSize(test, size) <= maxWidth) {
      current = test;
    } else {
      if (current) lines.push(current);
      if (font.widthOfTextAtSize(word, size) > maxWidth) {
        current = pushChunked(word);
      } else {
        current = word;
      }
    }
  }
  if (current) lines.push(current);
  return lines.length ? lines : ['N/A'];
}

export class FormPdfBuilder {
  constructor(doc, font, boldFont, options) {
    this.doc = doc;
    this.font = font;
    this.boldFont = boldFont;
    this.options = options;
    this.page = null;
    this.y = 0;
    this.contentWidth = PAGE_W - MARGIN * 2;
    this.valueX = MARGIN + 190;
  }

  static async create(options = {}) {
    const pdfDoc = await PDFDocument.create();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const builder = new FormPdfBuilder(pdfDoc, font, boldFont, options);
    if (options.letterhead?.logo) {
      builder.logoImage = await pdfDoc.embedPng(options.letterhead.logo);
    }
    builder.newPage();
    builder.drawHeader();
    return builder;
  }

  newPage() {
    this.page = this.doc.addPage([PAGE_W, PAGE_H]);
    this.y = PAGE_H - MARGIN;
  }

  ensureSpace(height) {
    if (this.y - height < MARGIN + 28) {
      this.newPage();
      this.drawContinuationHeader();
    }
  }

  text(str, x, y, size, font, color) {
    const safe = sanitizePdfText(str);
    if (!safe) return;
    this.page.drawText(safe, { x, y, size, font, color });
  }

  drawHeader() {
    if (this.options.letterhead) {
      this.drawLetterhead();
      return;
    }
    const { title, ref, subtitle, titleColor } = this.options;
    if (title) {
      const color = titleColor || PDF_COLORS.navy;
      const lines = wrapText(title, this.boldFont, 18, this.contentWidth);
      lines.forEach((line) => {
        const w = this.boldFont.widthOfTextAtSize(line, 18);
        this.page.drawText(line, {
          x: (PAGE_W - w) / 2,
          y: this.y,
          size: 18,
          font: this.boldFont,
          color,
        });
        this.y -= 22;
      });
    }
    if (ref) {
      const w = this.font.widthOfTextAtSize(ref, 9);
      this.page.drawText(ref, {
        x: (PAGE_W - w) / 2,
        y: this.y,
        size: 9,
        font: this.font,
        color: PDF_COLORS.muted,
      });
      this.y -= 16;
    }
    if (subtitle) {
      const lines = wrapText(subtitle, this.boldFont, 11, this.contentWidth);
      lines.forEach((line) => {
        const w = this.boldFont.widthOfTextAtSize(line, 11);
        this.page.drawText(line, {
          x: (PAGE_W - w) / 2,
          y: this.y,
          size: 11,
          font: this.boldFont,
          color: this.options.subtitleColor || PDF_COLORS.muted,
        });
        this.y -= 14;
      });
    }
    this.page.drawLine({
      start: { x: MARGIN, y: this.y },
      end: { x: PAGE_W - MARGIN, y: this.y },
      thickness: 1.5,
      color: PDF_COLORS.navy,
    });
    this.y -= 22;
  }

  // Externally issued documents: logo and company details, brand rules, then
  // the title alongside a boxed panel of reference numbers.
  drawLetterhead() {
    const { title, references = [], letterhead } = this.options;
    const right = PAGE_W - MARGIN;
    const logoSize = 88;
    const top = PAGE_H - 36;

    if (this.logoImage) {
      this.page.drawImage(this.logoImage, { x: MARGIN, y: top - logoSize, width: logoSize, height: logoSize });
    }

    let lineY = top - 24;
    (letterhead.lines || []).forEach((raw, i) => {
      const line = sanitizePdfText(raw);
      const font = i === 0 ? this.boldFont : this.font;
      const size = i === 0 ? 14 : 9.5;
      this.page.drawText(line, {
        x: right - font.widthOfTextAtSize(line, size),
        y: lineY,
        size,
        font,
        color: i === 0 ? PDF_COLORS.navy : PDF_COLORS.muted,
      });
      lineY -= i === 0 ? 17 : 13;
    });

    this.y = top - logoSize - 10;
    this.page.drawLine({ start: { x: MARGIN, y: this.y }, end: { x: right, y: this.y }, thickness: 2, color: PDF_COLORS.navy });
    this.y -= 3.5;
    this.page.drawLine({ start: { x: MARGIN, y: this.y }, end: { x: right, y: this.y }, thickness: 1, color: PDF_COLORS.brandGreen });
    this.y -= 18;

    const blockTop = this.y;
    const refBoxWidth = references.length ? 180 : 0;
    const refRowHeight = 30;
    const refBoxHeight = references.length * refRowHeight;
    if (references.length) {
      const boxX = right - refBoxWidth;
      this.page.drawRectangle({
        x: boxX,
        y: blockTop - refBoxHeight,
        width: refBoxWidth,
        height: refBoxHeight,
        color: PDF_COLORS.panel,
        borderColor: PDF_COLORS.navy,
        borderWidth: 0.8,
      });
      references.forEach(({ label, value }, i) => {
        const rowTop = blockTop - i * refRowHeight;
        if (i > 0) {
          this.page.drawLine({ start: { x: boxX, y: rowTop }, end: { x: right, y: rowTop }, thickness: 0.6, color: PDF_COLORS.line });
        }
        this.text(String(label).toUpperCase(), boxX + 10, rowTop - 11, 7, this.font, PDF_COLORS.muted);
        this.text(value, boxX + 10, rowTop - 24, 11, this.boldFont, PDF_COLORS.navy);
      });
    }

    const titleSize = 20;
    const titleLineHeight = 24;
    const titleLines = title
      ? wrapText(title, this.boldFont, titleSize, this.contentWidth - refBoxWidth - 16)
      : [];
    const regionHeight = Math.max(refBoxHeight, titleLines.length * titleLineHeight);
    let titleY = blockTop - regionHeight / 2 + ((titleLines.length - 1) * titleLineHeight) / 2 - 7;
    titleLines.forEach((line) => {
      this.page.drawText(line, { x: MARGIN, y: titleY, size: titleSize, font: this.boldFont, color: PDF_COLORS.navy });
      titleY -= titleLineHeight;
    });

    this.y = blockTop - regionHeight - 26;
  }

  drawContinuationHeader() {
    const title = this.options.title || 'Form';
    if (this.logoImage) {
      const size = 30;
      this.page.drawImage(this.logoImage, { x: MARGIN, y: this.y - 10, width: size, height: size });
      this.text(`${title} (continued)`, MARGIN + size + 8, this.y + 1, 10, this.boldFont, PDF_COLORS.navy);
      const refValue = sanitizePdfText(this.options.references?.[0]?.value);
      if (refValue) {
        const w = this.font.widthOfTextAtSize(refValue, 9);
        this.page.drawText(refValue, { x: PAGE_W - MARGIN - w, y: this.y + 1, size: 9, font: this.font, color: PDF_COLORS.muted });
      }
      this.y -= 16;
      this.page.drawLine({
        start: { x: MARGIN, y: this.y },
        end: { x: PAGE_W - MARGIN, y: this.y },
        thickness: 0.75,
        color: PDF_COLORS.navy,
      });
      this.y -= 22;
      return;
    }
    this.page.drawText(`${sanitizePdfText(title)} (continued)`, {
      x: MARGIN,
      y: this.y,
      size: 10,
      font: this.boldFont,
      color: PDF_COLORS.navy,
    });
    this.y -= 8;
    this.page.drawLine({
      start: { x: MARGIN, y: this.y },
      end: { x: PAGE_W - MARGIN, y: this.y },
      thickness: 0.75,
      color: PDF_COLORS.navy,
    });
    this.y -= 18;
  }

  banner(text, color = PDF_COLORS.red) {
    const lines = wrapText(text, this.boldFont, 12, this.contentWidth);
    this.ensureSpace(lines.length * 16 + 8);
    lines.forEach((line) => {
      const w = this.boldFont.widthOfTextAtSize(line, 12);
      this.page.drawText(line, {
        x: (PAGE_W - w) / 2,
        y: this.y,
        size: 12,
        font: this.boldFont,
        color,
      });
      this.y -= 16;
    });
    this.y -= 6;
  }

  section(title) {
    this.ensureSpace(28);
    this.page.drawText(sanitizePdfText(title), {
      x: MARGIN,
      y: this.y,
      size: 12,
      font: this.boldFont,
      color: PDF_COLORS.navy,
    });
    this.y -= 6;
    this.page.drawLine({
      start: { x: MARGIN, y: this.y },
      end: { x: PAGE_W - MARGIN, y: this.y },
      thickness: 0.6,
      color: PDF_COLORS.line,
    });
    this.y -= 16;
  }

  subsection(title) {
    this.ensureSpace(22);
    this.page.drawText(sanitizePdfText(title), {
      x: MARGIN,
      y: this.y,
      size: 11,
      font: this.boldFont,
      color: PDF_COLORS.navy,
    });
    this.y -= 16;
  }

  field(label, value, valueColor = PDF_COLORS.dark) {
    const labelMax = this.valueX - MARGIN - 8;
    const labelLines = wrapText(label, this.boldFont, 10, labelMax);
    const valueLines = wrapText(
      value === undefined || value === null || value === '' ? 'N/A' : value,
      this.font,
      10,
      PAGE_W - MARGIN - this.valueX
    );
    const lineCount = Math.max(labelLines.length, valueLines.length);
    const height = Math.max(18, lineCount * 13 + 6);
    this.ensureSpace(height);
    labelLines.forEach((line, i) => {
      this.page.drawText(line, {
        x: MARGIN,
        y: this.y - i * 13,
        size: 10,
        font: this.boldFont,
        color: PDF_COLORS.navy,
      });
    });
    valueLines.forEach((line, i) => {
      this.page.drawText(line, {
        x: this.valueX,
        y: this.y - i * 13,
        size: 10,
        font: this.font,
        color: valueColor,
      });
    });
    this.y -= height - 4;
    this.page.drawLine({
      start: { x: MARGIN, y: this.y },
      end: { x: PAGE_W - MARGIN, y: this.y },
      thickness: 0.4,
      color: PDF_COLORS.line,
    });
    this.y -= 12;
  }

  check(label, value) {
    this.field(label, formatYesNo(value), yesNoColor(value));
  }

  passFail(label, isPass) {
    const text = isPass === true ? 'PASS' : isPass === false ? 'FAIL' : 'N/A';
    this.field(label, text, passFailColor(isPass));
  }

  bullet(text) {
    const lines = wrapText(text, this.font, 9, this.contentWidth - 14);
    const height = lines.length * 12 + 4;
    this.ensureSpace(height);
    this.page.drawText('-', {
      x: MARGIN,
      y: this.y,
      size: 9,
      font: this.font,
      color: PDF_COLORS.dark,
    });
    lines.forEach((line, i) => {
      this.page.drawText(line, {
        x: MARGIN + 12,
        y: this.y - i * 12,
        size: 9,
        font: this.font,
        color: PDF_COLORS.dark,
      });
    });
    this.y -= height;
  }

  paragraph(text, { italic = false, size = 9, color = PDF_COLORS.muted } = {}) {
    const lines = wrapText(text, this.font, size, this.contentWidth);
    const height = lines.length * (size + 3) + 6;
    this.ensureSpace(height);
    lines.forEach((line, i) => {
      this.page.drawText(line, {
        x: MARGIN,
        y: this.y - i * (size + 3),
        size,
        font: this.font,
        color,
      });
    });
    this.y -= height;
    void italic;
  }

  longText(label, value) {
    if (label) {
      this.ensureSpace(18);
      this.page.drawText(sanitizePdfText(label), {
        x: MARGIN,
        y: this.y,
        size: 10,
        font: this.boldFont,
        color: PDF_COLORS.navy,
      });
      this.y -= 14;
    }
    this.paragraph(value || 'N/A', { size: 10, color: PDF_COLORS.dark });
  }

  table(headers, rows) {
    const colCount = headers.length;
    const colW = this.contentWidth / colCount;
    const rowH = 22;
    this.ensureSpace(rowH * (rows.length + 1) + 8);

    const drawRow = (cells, y, bold) => {
      cells.forEach((cell, i) => {
        const x = MARGIN + i * colW;
        this.page.drawRectangle({
          x,
          y: y - 6,
          width: colW,
          height: rowH,
          borderColor: PDF_COLORS.line,
          borderWidth: 0.6,
        });
        const text = sanitizePdfText(cell);
        const font = bold ? this.boldFont : this.font;
        const w = font.widthOfTextAtSize(text, 9);
        this.page.drawText(text, {
          x: x + (colW - w) / 2,
          y: y + 2,
          size: 9,
          font,
          color: PDF_COLORS.dark,
        });
      });
    };

    drawRow(headers, this.y, true);
    this.y -= rowH;
    rows.forEach((row) => {
      this.ensureSpace(rowH + 4);
      drawRow(row, this.y, false);
      this.y -= rowH;
    });
    this.y -= 10;
  }

  addressBlocks(blocks) {
    const gap = 14;
    const colWidth = (this.contentWidth - gap * (blocks.length - 1)) / blocks.length;
    const barHeight = 18;
    const lineHeight = 13;
    const maxLines = Math.max(...blocks.map((block) => block.lines.length));
    const boxHeight = barHeight + 14 + maxLines * lineHeight;
    this.ensureSpace(boxHeight + 18);
    const top = this.y + 8;

    blocks.forEach((block, i) => {
      const x = MARGIN + i * (colWidth + gap);
      this.page.drawRectangle({
        x,
        y: top - boxHeight,
        width: colWidth,
        height: boxHeight,
        borderColor: PDF_COLORS.line,
        borderWidth: 0.8,
      });
      this.page.drawRectangle({ x, y: top - barHeight, width: colWidth, height: barHeight, color: PDF_COLORS.navy });
      this.text(String(block.title).toUpperCase(), x + 10, top - barHeight + 6, 8, this.boldFont, PDF_COLORS.white);
      block.lines.forEach((line, j) => {
        this.text(
          line,
          x + 10,
          top - barHeight - 15 - j * lineHeight,
          10,
          j === 0 ? this.boldFont : this.font,
          PDF_COLORS.dark,
        );
      });
    });

    this.y = top - boxHeight - 24;
  }

  pageBreak() {
    this.newPage();
    this.drawContinuationHeader();
  }

  async image(title, bytes, mimeType, { maxHeight = 460 } = {}) {
    const embedded = mimeType === 'image/png'
      ? await this.doc.embedPng(bytes)
      : await this.doc.embedJpg(bytes);
    const scale = Math.min(this.contentWidth / embedded.width, maxHeight / embedded.height, 1);
    const width = embedded.width * scale;
    const height = embedded.height * scale;
    // Reserve room for the title too so it never sits orphaned above a page break.
    this.ensureSpace(height + (title ? 28 : 0) + 12);
    if (title) this.section(title);
    const x = MARGIN + (this.contentWidth - width) / 2;
    this.page.drawImage(embedded, { x, y: this.y - height, width, height });
    this.page.drawRectangle({
      x,
      y: this.y - height,
      width,
      height,
      borderColor: PDF_COLORS.line,
      borderWidth: 0.8,
    });
    this.y -= height + 12;
  }

  spacer(n = 8) {
    this.y -= n;
  }

  drawFooters() {
    const footer = sanitizePdfText(this.options.letterhead?.footer);
    if (!footer) return;
    const pages = this.doc.getPages();
    pages.forEach((page, i) => {
      page.drawLine({
        start: { x: MARGIN, y: MARGIN - 6 },
        end: { x: PAGE_W - MARGIN, y: MARGIN - 6 },
        thickness: 0.5,
        color: PDF_COLORS.line,
      });
      page.drawText(footer, { x: MARGIN, y: MARGIN - 18, size: 8, font: this.font, color: PDF_COLORS.muted });
      const pageLabel = `Page ${i + 1} of ${pages.length}`;
      const w = this.font.widthOfTextAtSize(pageLabel, 8);
      page.drawText(pageLabel, { x: PAGE_W - MARGIN - w, y: MARGIN - 18, size: 8, font: this.font, color: PDF_COLORS.muted });
    });
  }

  async toBlob() {
    const generated = `Generated ${new Date().toLocaleString('en-GB', { timeZone: 'Europe/London' })}`;
    this.ensureSpace(16);
    this.page.drawText(generated, {
      x: MARGIN,
      y: Math.max(this.y, MARGIN),
      size: 8,
      font: this.font,
      color: PDF_COLORS.muted,
    });
    this.drawFooters();
    const bytes = await this.doc.save();
    assertRealPdf(bytes);
    return new Blob([bytes], { type: 'application/pdf' });
  }
}

const PDF_MAGIC = [0x25, 0x50, 0x44, 0x46, 0x2d]; // %PDF-

// A file named .pdf is worthless if the bytes are a zip (PK) container, so the
// signature is checked before the document is ever handed off for delivery.
export function assertRealPdf(bytes) {
  const head = bytes instanceof ArrayBuffer ? new Uint8Array(bytes) : bytes;
  const isPdf = PDF_MAGIC.every((byte, i) => head[i] === byte);
  if (isPdf) return true;
  const isZip = head[0] === 0x50 && head[1] === 0x4b;
  throw new Error(
    isZip
      ? 'Refusing to deliver form: output is a ZIP/Office container (PK), not a PDF.'
      : 'Refusing to deliver form: output is missing the %PDF- signature.'
  );
}

export async function createFormPdf(options, buildFn) {
  const pdf = await FormPdfBuilder.create(options);
  await buildFn(pdf);
  return pdf.toBlob();
}
